/**
 * Duration Store (Zustand)
 *
 * Holds the worksheet display range, investigate range, and auto update settings.
 */

import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { toRangeExport } from '@/trendData/durationRange.utilities';
import {
  AUTO_UPDATE,
  DEFAULT_DISPLAY_RANGE_DURATION_DAYS,
  DEFAULT_INVESTIGATE_RANGE_DURATION_DAYS,
} from '@/trendData/trendData.constants';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  DurationActions,
  DurationDehydratedState,
  DurationState,
  UpdateRangeTimesPayload,
} from '@/trendData/duration.types';

/**
 * Builds the default state for the given time. `displayPixels` is kept across a rehydrate since it comes from the
 * chart rather than the worksheet.
 */
function buildDefaultDurationState(now: number, displayPixels = 0): DurationState {
  return {
    autoUpdate: {
      mode: AUTO_UPDATE.MODES.OFF,
      offset: 0,
      manualInterval: { ...AUTO_UPDATE.DEFAULT_INTERVAL },
      displayPixels,
      now,
    },
    displayRangeOriginal: undefined,
    investigateRangeOriginal: undefined,
    displayRange: {
      start: moment.utc(now).subtract(DEFAULT_DISPLAY_RANGE_DURATION_DAYS, 'days').valueOf(),
      end: now,
    },
    investigateRange: {
      start: moment.utc(now).subtract(DEFAULT_INVESTIGATE_RANGE_DURATION_DAYS, 'days').valueOf(),
      end: now,
    },
  };
}

/**
 * Auto update should not create worksteps, but it changes the same fields a user would change through the UI. The
 * range is saved off before auto update first changes it and that copy is dehydrated instead. Any other change
 * discards the copy.
 */
function applyRangeTimes(
  draft: DurationState,
  rangeKey: 'displayRange' | 'investigateRange',
  { start, end, isAutoUpdate }: UpdateRangeTimesPayload,
) {
  const originalKey = rangeKey === 'displayRange' ? 'displayRangeOriginal' : 'investigateRangeOriginal';
  if (isAutoUpdate && !draft[originalKey]) {
    draft[originalKey] = { start: draft[rangeKey].start, end: draft[rangeKey].end };
  }
  if (!isAutoUpdate) {
    draft[originalKey] = undefined;
  }

  draft[rangeKey].start = moment.utc(start).valueOf();
  draft[rangeKey].end = moment.utc(end).valueOf();
}

export const dehydrateDurationState = (state: DurationState): DurationDehydratedState => {
  const { mode, offset, manualInterval } = state.autoUpdate;
  const isAutoUpdating = state.autoUpdate.mode !== AUTO_UPDATE.MODES.OFF;
  const displayRange = (isAutoUpdating && state.displayRangeOriginal) || state.displayRange;
  const investigateRange = (isAutoUpdating && state.investigateRangeOriginal) || state.investigateRange;

  return {
    autoUpdate: { mode, offset, manualInterval },
    displayRange: { start: displayRange.start, end: displayRange.end },
    investigateRange: { start: investigateRange.start, end: investigateRange.end },
  };
};

const durationActionsFactory: ActionsFactory<DurationState, DurationActions> = (set, get, resetState) => ({
  setNow: (now) =>
    set((draft) => {
      draft.autoUpdate.now = now;
    }),

  setMode: (mode) =>
    set((draft) => {
      draft.autoUpdate.mode = mode;
    }),

  computeOffset: () =>
    set((draft) => {
      draft.autoUpdate.offset = draft.displayRange.end - draft.autoUpdate.now;
    }),

  clearOffset: () =>
    set((draft) => {
      draft.autoUpdate.offset = 0;
    }),

  setManualInterval: (manualInterval) =>
    set((draft) => {
      draft.autoUpdate.manualInterval = manualInterval;
    }),

  setDisplayPixels: (displayPixels) =>
    set((draft) => {
      draft.autoUpdate.displayPixels = displayPixels;
    }),

  updateDisplayRangeTimes: (payload) =>
    set((draft) => {
      applyRangeTimes(draft, 'displayRange', payload);
    }),

  updateInvestigateRangeTimes: (payload) =>
    set((draft) => {
      applyRangeTimes(draft, 'investigateRange', payload);
    }),

  rehydrate: (dehydratedState) => {
    const defaults = buildDefaultDurationState(moment.utc().valueOf(), get().autoUpdate.displayPixels);

    set((draft) => {
      Object.assign(draft, defaults);
      const { mode, offset, manualInterval } = dehydratedState.autoUpdate ?? {};
      if (mode !== undefined) {
        draft.autoUpdate.mode = mode;
      }
      if (offset !== undefined) {
        draft.autoUpdate.offset = offset;
      }
      if (manualInterval !== undefined) {
        draft.autoUpdate.manualInterval = manualInterval;
      }
      if (dehydratedState.investigateRange) {
        applyRangeTimes(draft, 'investigateRange', { ...dehydratedState.investigateRange, isAutoUpdate: false });
      }
      if (dehydratedState.displayRange) {
        applyRangeTimes(draft, 'displayRange', { ...dehydratedState.displayRange, isAutoUpdate: false });
      }
    });
  },

  reset: () => resetState(buildDefaultDurationState(moment.utc().valueOf())),
});

export const initialState: DurationState = Object.freeze(buildDefaultDurationState(moment.utc().valueOf()));

const STORE_NAME = 'Duration';

const [DurationStoreProvider, useDurationStoreInstance, useDurationSelector] = createStore(
  STORE_NAME,
  initialState,
  durationActionsFactory,
);

export { DurationStoreProvider };

/**
 * The display range behind useDisplayRange(). Memoized on the start and end times, so components get the same
 * object and do not re-render until the range changes. The object is shared, so clone the moments before changing
 * them. Code outside React uses getDurationDisplayRange() from duration.utilities.
 */
const selectDisplayRange = createSelector(
  (state: DurationState) => state.displayRange.start,
  (state: DurationState) => state.displayRange.end,
  toRangeExport,
);

/**
 * The investigate range behind useInvestigateRange(). Memoized on the start and end times, so components get the same
 * object and do not re-render until the range changes. The object is shared, so clone the moments before changing
 * them. Code outside React uses getDurationInvestigateRange() from duration.utilities.
 */
const selectInvestigateRange = createSelector(
  (state: DurationState) => state.investigateRange.start,
  (state: DurationState) => state.investigateRange.end,
  toRangeExport,
);

export const useDisplayRange = () => useDurationSelector(selectDisplayRange);

export const useInvestigateRange = () => useDurationSelector(selectInvestigateRange);

export const useInvestigateRangeOriginal = () => useDurationSelector((state) => state.investigateRangeOriginal);

export const useAutoUpdate = () => useDurationSelector((state) => state.autoUpdate);

export const useAutoUpdateMode = () => useDurationSelector((state) => state.autoUpdate.mode);

export const useDurationActions = () => useDurationStoreInstance().actions;

/**
 * Registers the duration store for persistence. A workstep is pushed whenever the dehydrated state changes. Auto
 * update does not create worksteps because, while it is on, the range from before it started is what is dehydrated.
 */
export const usePersistDurationStore = () => {
  const register = useRegisterPersistedStore();
  const store = useDurationStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqDurationStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateDurationState,
      rehydrate: (dehydratedState: DurationDehydratedState) => store.actions.rehydrate(dehydratedState),
    });
  }, [register, store]);
};

export type DurationStore = StoreInstance<DurationState, DurationActions>;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateDurationStore(initialStateOverrides?: Partial<DurationState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    durationActionsFactory,
    { storeName: STORE_NAME },
  );
}
